/**
 * AI CONTEXT
 * Purpose: middle TeacherView panel for attendance operations by selected session.
 * Composes: SessionProjectInfo, NextProjectInfo, TeacherAttendanceSection groups.
 * Inputs: TeacherViewContext + Firebase attendance mutator.
 * Used by: TeacherView.
 * Refactor: move category-building logic to hook for easier testing.
 */
import { uniq } from "lodash"
import { AnimatePresence } from "motion/react"
import { useCallback, useMemo } from "react"

import { classSessionAtoms } from "#atoms"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import EmptyState from "#blocks/EmptyState"
import Flex from "#blocks/Flex"
import { CLASS_GROUPS } from "#constants/classOptions"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { useFirebaseContext } from "#contexts/FirebaseContextProvider"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { AttendanceRowData } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { Attendance, AttendanceStatus } from "#customTypes/types"
import { useSomeDocs } from "#hooks/jotai/useSubscription"
import { getSessionInfoFromId, isDefined } from "#utils/classHelpers"
import { isStudentProjectOnTrack } from "#utils/studentHelpers"

import NextProjectInfo from "./NextProjectInfo"
import SessionProjectInfo from "./SessionProjectInfo"
import TeacherAttendanceSection from "./TeacherAttendanceSection"
import { useTeacherViewContext } from "./TeacherViewContextProvider"

const getAttendanceStatus = (attendance: Attendance, sessionId: Uuid) => {
  if (!sessionId) return ATTENDANCE_STATUSES.INVALID

  const [classId, sessionIndex] = getSessionInfoFromId(sessionId)
  return (
    attendance?.[classId]?.[sessionIndex]?.status ?? ATTENDANCE_STATUSES.INVALID
  )
}

const AttendanceSection = () => {
  const { selectedSession, studentsById, studentsByExceptionType } =
    useTeacherViewContext()

  const { onStudentAttendanceChange } = useFirebaseContext()

  const currentSessionProjectId = selectedSession?.session?.projectId

  const missedSessionIds = useMemo(() => {
    const ids =
      studentsByExceptionType.makeups
        ?.filter(({ sessionId }) => sessionId === selectedSession?.id)
        .map(({ exceptionData }) => exceptionData?.missedSessionId)
        .filter((id): id is Uuid => typeof id === "string") ?? []
    return uniq(ids)
  }, [studentsByExceptionType.makeups, selectedSession?.id])

  const missedSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    missedSessionIds
  )

  const projectIdBySessionId = useMemo(() => {
    const map: Partial<Record<Uuid, Uuid>> = {}
    missedSessions.forEach((missedSession) => {
      if (missedSession?.id && missedSession.projectId) {
        map[missedSession.id] = missedSession.projectId
      }
    })
    return map
  }, [missedSessions])

  const studentsByCategory = useMemo(() => {
    const { assistants, makeups, tryouts, plannedAbsences } =
      studentsByExceptionType
    const { studentIds } = selectedSession?.session || {}
    const currentSession = selectedSession?.session

    const allEnrolled: AttendanceRowData[] =
      selectedSession?.id && studentIds?.length
        ? studentIds
            ?.map((id) => {
              const student = studentsById[id]
              if (!student) return undefined

              return {
                student,
                status: getAttendanceStatus(
                  student.attendance || {},
                  selectedSession.id
                ),
                sessionId: selectedSession.id,
                projectId: currentSessionProjectId,
              }
            })
            .filter(isDefined)
        : []

    const students: AttendanceRowData[] = []
    const exceptions: AttendanceRowData[] = []

    allEnrolled.forEach((row) => {
      if (
        currentSession &&
        !isStudentProjectOnTrack(row.student, currentSession, row.status)
      ) {
        exceptions.push(row)
      } else {
        students.push(row)
      }
    })

    return {
      students,
      exceptions,
      assistants: assistants
        .filter((exception) => exception.sessionId === selectedSession?.id)
        .map(({ student, sessionId }) => ({
          student,
          status: getAttendanceStatus(
            student?.assistantAttendance || {},
            sessionId
          ),
          sessionId,
          projectId: currentSessionProjectId,
        })),
      makeups: makeups
        .filter((exception) => exception.sessionId === selectedSession?.id)
        .map(({ student, exceptionData }) => {
          const missedSessionId = exceptionData!.missedSessionId as Uuid
          return {
            student,
            status: getAttendanceStatus(
              student?.attendance || {},
              missedSessionId
            ),
            sessionId: missedSessionId,
            projectId: projectIdBySessionId[missedSessionId],
          }
        }),
      tryouts: tryouts
        .filter((exception) => exception.sessionId === selectedSession?.id)
        .map(({ student, sessionId }) => ({
          student,
          status: getAttendanceStatus(student?.attendance || {}, sessionId),
          sessionId,
          projectId: currentSessionProjectId,
        })),
      plannedAbsences: plannedAbsences
        .filter((exception) => exception.sessionId === selectedSession?.id)
        .map(({ student, sessionId }) => ({
          student,
          status: getAttendanceStatus(student?.attendance || {}, sessionId),
          sessionId,
          projectId: currentSessionProjectId,
        })),
    }
  }, [
    selectedSession,
    studentsById,
    studentsByExceptionType,
    currentSessionProjectId,
    projectIdBySessionId,
  ])

  const handleStudentAttendanceChange = useCallback(
    (
      student: StudentConstructor,
      status: AttendanceStatus,
      sessionId: Uuid,
      projectId?: Uuid,
      attendanceField: "attendance" | "assistantAttendance" = "attendance"
    ) => {
      const [classId, sessionIndex] = getSessionInfoFromId(sessionId)
      onStudentAttendanceChange(
        student,
        classId,
        sessionIndex,
        status,
        attendanceField,
        projectId
      )
    },
    [onStudentAttendanceChange]
  )

  const { students, assistants, makeups, tryouts, exceptions } =
    studentsByCategory
  const classGroup = selectedSession?.classGroup ?? CLASS_GROUPS.CORE

  if (!selectedSession?.id) return null

  const hasNoStudents =
    !students.length &&
    !assistants.length &&
    !makeups.length &&
    !tryouts.length &&
    !exceptions.length

  if (hasNoStudents) {
    return <EmptyState description="No students enrolled in this session" />
  }

  return (
    <AnimateInFlex
      wait={500}
      animation={ANIMATION_BY_KEY.down}
      col
      gap={14}
      style={{
        overflowY: "scroll",
        padding: "10px 40px 30px",
        height: "calc(100vh - 200px)",
      }}
    >
      <AnimatePresence mode="popLayout">
        <TeacherAttendanceSection
          key="tryouts"
          type="tryouts"
          students={tryouts}
          sessionId={selectedSession?.id}
          session={selectedSession?.session}
          classGroup={classGroup}
          simplifiedProfile
          onStudentAttendanceChange={handleStudentAttendanceChange}
        />

        <TeacherAttendanceSection
          key="makeups"
          type="makeups"
          students={makeups}
          sessionId={selectedSession?.id}
          session={selectedSession?.session}
          classGroup={classGroup}
          simplifiedProfile
          onStudentAttendanceChange={handleStudentAttendanceChange}
        />

        <TeacherAttendanceSection
          key="exceptions"
          type="exceptions"
          students={exceptions}
          sessionId={selectedSession?.id}
          session={selectedSession?.session}
          classGroup={classGroup}
          simplifiedProfile
          onStudentAttendanceChange={handleStudentAttendanceChange}
        />

        <TeacherAttendanceSection
          key="students"
          type="students"
          students={students}
          sessionId={selectedSession?.id}
          session={selectedSession?.session}
          classGroup={classGroup}
          simplifiedProfile
          onStudentAttendanceChange={handleStudentAttendanceChange}
        />

        <TeacherAttendanceSection
          key="assistants"
          type="assistants"
          students={assistants}
          sessionId={selectedSession?.id}
          session={selectedSession?.session}
          classGroup={classGroup}
          simplifiedProfile
          onStudentAttendanceChange={(student, status, sessionId, projectId) =>
            handleStudentAttendanceChange(
              student,
              status,
              sessionId,
              projectId,
              "assistantAttendance"
            )
          }
        />
      </AnimatePresence>
    </AnimateInFlex>
  )
}

const TeacherAttendance = () => {
  const { body1, body2, altColor } = useThemeContext()
  const { selectedSession } = useTeacherViewContext()

  const { project, session, notes } = selectedSession || {}

  return (
    <Flex
      style={{
        width: "100%",
        height: "100%",
        background: `linear-gradient(to right, ${body1}, ${body2})`,
        overflowY: "auto",
      }}
    >
      <Flex
        col
        style={{
          height: "80vh",
          width: 800,
          paddingTop: 6,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {selectedSession ? (
          <>
            <ComponentErrorBoundary variant="line" context="Project">
              <SessionProjectInfo session={session} project={project} />
            </ComponentErrorBoundary>
            <ComponentErrorBoundary variant="line" context="Next Project">
              <NextProjectInfo />
            </ComponentErrorBoundary>
            {notes ? (
              <AnimateInFlex
                animation={ANIMATION_BY_KEY.down}
                style={{
                  fontSize: 12,
                  backgroundColor: altColor,
                  color: "white",
                  padding: "4px 10px",
                  borderRadius: "4px",
                  margin: "0px 44px",
                }}
              >
                {notes}
              </AnimateInFlex>
            ) : null}
            <AttendanceSection />
          </>
        ) : (
          <>
            <EmptyState description="No sessions found" />
            <Flex center>
              NOTE: This only shows sessions for the current user. If a teacher
              isn't signed in, nothing will show up here.
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default TeacherAttendance
